import React from "react"
import {ClickAwayListener, Divider} from "@mui/material"
import {UxCTA as CTA} from "@next/ux-cta"
import {formatTextTestIds} from "@next/utils"
import {breakpoints} from "@next/ux-fabric"
import {useMediaQuery} from "../../../../hooks/useMediaQuery"
import CountrySelectDropdown from "../../../CountrySelectDropdown"
import CountrySelectLanguageDropdown from "../../../CountrySelectLanguageDropdown"
import {CountryLanguage} from "../../../../models/countryselector"
import {CHEVRON_ICON_URL, COUNTRY_LANG_SELECTOR_DATA_GA} from "../../../../config/constants"
import {
    CountrySelectorContainer,
    CountrySelectorContainerHeader,
    CountrySelectorContainerElement,
    CountrySelectorCTAWrapper,
    LanguageButtonsContainer,
    LanguageButton,
    CloseButton,
    Title,
    CountrySelectorBottomText,
    StayButton,
    StyledIcon,
    StayText,
    CountrySelectorROW,
    CountrySelectorLink,
    DrawerContainer,
    InputLabel,
    ROWLink,
} from "./component"
import env from "../../../../config/env"
import type {DefaultTemplatePropType} from "../../index"
import {getAnchorDirection} from "../../util"

export const Club25CountryLangSelector = ({
    selectedCountry,
    selectedLanguage,
    selectLanguage,
    shopNowText,
    onCTAClick,
    headerTitle,
    closeText,
    closePanel,
    stayText,
    isOpeningFromBottom,
    isInternationalCountry,
    textAlignment,
    locationText,
    ROWText,
    ROWLinkText,
    ROWLinkUrl,
    languageText,
    isOpenCountrySelectorDrawer,
    keepMounted,
    disablePortal,
    hideBackdrop,
    disableScrollLock,
    isShowOverlay,
    territory,
}: DefaultTemplatePropType) => {
    const CHEVRON_ICON = `${env.APP_BLOB_STORAGE_PATH}${CHEVRON_ICON_URL}`
    const isLGUp = useMediaQuery(`(min-width:${breakpoints.values.lg.toString()}px)`)

    return (
        <DrawerContainer
            data-testid={formatTextTestIds(`header-drawer-country-selector`)}
            anchor={getAnchorDirection(isOpeningFromBottom)}
            open={isOpenCountrySelectorDrawer}
            onClose={closePanel}
            ModalProps={{keepMounted, disablePortal, hideBackdrop, disableScrollLock}}
            style={{position: isShowOverlay ? "fixed" : "initial"}}
        >
            <ClickAwayListener onClickAway={closePanel}>
                <CountrySelectorContainer
                    data-testid={formatTextTestIds(`header-drawer-country-selector-container`)}
                    isIntCountry={isInternationalCountry}
                >
                    <CountrySelectorContainerHeader>
                        <Title variant="subtitle2">{headerTitle}</Title>
                        <CloseButton disableRipple data-testid="country-selector-close-button" onClick={closePanel}>
                            {closeText}
                        </CloseButton>
                    </CountrySelectorContainerHeader>

                    <CountrySelectorContainerElement>
                        {locationText && <InputLabel>{locationText}</InputLabel>}
                        <CountrySelectDropdown />
                    </CountrySelectorContainerElement>

                    <CountrySelectorContainerElement>
                        {languageText && <InputLabel>{languageText}</InputLabel>}
                        <LanguageButtonsContainer>
                            {/* eslint-disable-next-line react/display-name */}
                            {(selectedCountry?.Languages.length ?? 0) > 2 ? (
                                <CountrySelectLanguageDropdown
                                    territory={territory}
                                    textAlignment={textAlignment}
                                    selectedCountry={selectedCountry}
                                    selectLanguage={selectLanguage}
                                    selectedLanguage={selectedLanguage}
                                />
                            ) : (
                                selectedCountry?.Languages.map((lan: CountryLanguage, i) => (
                                    <LanguageButton
                                        key={lan.AccountDomainUrl}
                                        selected={selectedLanguage === lan.Name}
                                        onClick={() => selectLanguage(lan.Name)}
                                        data-testid={`country-selector-language-button-${i}`}
                                        data-ga-v1={COUNTRY_LANG_SELECTOR_DATA_GA}
                                        data-ga-v2={selectedCountry.Country}
                                        data-ga-v3={lan.Value}
                                    >
                                        {lan.Value}
                                    </LanguageButton>
                                ))
                            )}
                        </LanguageButtonsContainer>
                    </CountrySelectorContainerElement>
                    <CountrySelectorCTAWrapper>
                        <CTA
                            text={shopNowText}
                            color="primary"
                            state="enabled"
                            variant="contained"
                            size={isLGUp ? "medium" : "large"}
                            onClick={onCTAClick}
                            testId="country-selector-CTA-button"
                        />
                    </CountrySelectorCTAWrapper>
                    <Divider variant="middle" />
                    {!isInternationalCountry && (
                        <CountrySelectorBottomText>
                            <StayButton onClick={closePanel} data-testid="country-selector-stay-text">
                                <StayText variant="body3">{stayText}</StayText>
                                <StyledIcon textAlignment={textAlignment} src={CHEVRON_ICON} />
                            </StayButton>
                        </CountrySelectorBottomText>
                    )}
                    {ROWText && (
                        <CountrySelectorROW>
                            <ROWLink variant="body1">{ROWText}</ROWLink>
                            <CountrySelectorLink
                                data-testid="country-selector-ROWLink"
                                href={ROWLinkUrl}
                                underline="always"
                            >
                                {ROWLinkText}
                            </CountrySelectorLink>
                        </CountrySelectorROW>
                    )}
                </CountrySelectorContainer>
            </ClickAwayListener>
        </DrawerContainer>
    )
}

Club25CountryLangSelector.displayName = "Club25CountryLangSelector"

export default Club25CountryLangSelector
